import { Box, Flex, Text, Menu, MenuButton, MenuList, MenuItem, MenuDivider, Link } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';

import IconSvg from 'ui/shared/IconSvg';

interface User {
  id: number;
  username: string;
  email: string;
  isAdmin?: boolean;
}

const UserMenu = () => {
  const router = useRouter();
  const [ user, setUser ] = useState<User | null>(null);
  const [ isLoading, setIsLoading ] = useState(true);

  useEffect(() => {
    // Check for auth token and user in localStorage
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData) as User);
      } catch (e) {
        // Invalid user data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    // Clear auth cookie
    document.cookie = 'authToken=; path=/; max-age=0; SameSite=Lax';
    setUser(null);
    // Only redirect to homepage if on a protected (account/admin/auth) page
    // Otherwise stay on the current page
    const protectedPrefixes = ['/account', '/admin', '/auth'];
    const isProtectedPage = protectedPrefixes.some(prefix => router.pathname.startsWith(prefix));
    if (isProtectedPage) {
      router.push('/');
    }
    // else: stay on current page (just clear user state above triggers re-render)
  };

  // Loading state
  if (isLoading) {
    return null;
  }

  // Not logged in - show Sign In
  if (!user) {
    return (
      <Box
        as="a"
        href="/signin"
        display="flex"
        alignItems="center"
        gap="6px"
        fontSize="14px"
        fontWeight={ 400 }
        color="#6c757d"
        _hover={{ color: '#0784c3', textDecoration: 'none' }}
      >
        <Text>@</Text>
        <Text>Sign In</Text>
      </Box>
    );
  }

  // Logged in - show username with dropdown
  return (
    <Menu isLazy placement="bottom-end" gutter={ 4 }>
      <MenuButton
        as={ Flex }
        alignItems="center"
        gap="4px"
        fontSize="14px"
        fontWeight={ 500 }
        color="#0784c3"
        cursor="pointer"
        _hover={{ color: '#066a9c' }}
        whiteSpace="nowrap"
      >
        <Text as="span">@</Text>
        <Text as="span" textTransform="uppercase">{ user.username }</Text>
        <IconSvg name="arrows/east-mini" boxSize="14px" transform="rotate(90deg)" ml="2px"/>
      </MenuButton>

      <MenuList
        className="UserMenu-dropdown NavLinkGroup-dropdown"
        bg="card_bg"
        border="1px solid"
        borderColor="card_border"
        borderTopWidth="3px"
        borderTopColor="link_accent"
        borderRadius="sm"
        boxShadow="card"
        py={ 0 }
        mt={3}
        minW="220px"
        zIndex={ 1100 }
        overflow="hidden"
      >
        { /* Blue top bar - same as Explore/Developers/More dropdowns */ }
        {/* <Box h="3px" minH="3px" bg="link_accent" flexShrink={ 0 } aria-hidden/> */}
        <Box p={ 2 } py={ 1 } px={ 2 }>
          { /* Profile Section - same text/hover as other nav dropdowns */ }
          <MenuItem
            as={ Link }
            href="/account/profile"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            My Profile
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/watchlist"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Watch List
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/private-tags"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Txn Private Notes
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/tag-address"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Private Name Tags
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/token-ignore"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Token Ignore List
          </MenuItem>

          <MenuDivider borderColor="divider" my={ 2 }/>

          { /* Developer Section */ }
          <MenuItem
            as={ Link }
            href="/account/api-keys"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            API Dashboard
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/verified-addresses"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Verified Address
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/custom-abi"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Custom ABI
          </MenuItem>

          <MenuItem
            as={ Link }
            href="/account/advanced-filter"
            fontSize="0.8125rem"
            fontWeight={ 400 }
            color="text_primary"
            _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
            _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
            py={ 1.5 }
            px={ 3 }
          >
            Advanced Filter
          </MenuItem>

          { /* Admin Section - same style as other items (not purple) */ }
          { user.isAdmin && (
            <>
              <MenuDivider borderColor="divider" my={ 2 }/>
              <MenuItem
                as={ Link }
                href="/admin"
                fontSize="0.8125rem"
                fontWeight={ 400 }
                color="text_primary"
                _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
                _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
                py={ 1.5 }
                px={ 3 }
              >
                🛡️ Admin Dashboard
              </MenuItem>
              <MenuItem
                as={ Link }
                href="/admin/users"
                fontSize="0.8125rem"
                fontWeight={ 400 }
                color="text_primary"
                _hover={{ bg: 'gray.50', color: 'link_accent', textDecoration: 'none' }}
                _dark={{ _hover: { bg: 'whiteAlpha.200', color: 'link_accent' } }}
                py={ 1.5 }
                px={ 3 }
              >
                👥 Manage Users
              </MenuItem>
            </>
          ) }

          <MenuDivider borderColor="divider" my={ 2 }/>

          { /* Sign Out - bordered button with arrow (per reference image) */ }
          <Box pt={ 1 } pb={ 2 } px={ 0 }>
            <Box
              as="button"
              type="button"
              onClick={ handleSignOut }
              w="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={ 2 }
              py={ 2 }
              px={ 4 }
              fontSize="14px"
              fontWeight={ 500 }
              color="link_accent"
              bg="transparent"
              border="1px solid"
              borderColor="link_accent"
              borderRadius="sm"
              cursor="pointer"
              _hover={{ bg: 'blue.50', borderColor: 'link_accent' }}
              _dark={{ _hover: { bg: 'whiteAlpha.100' } }}
            >
              <IconSvg name="sign_out" color="link_accent" boxSize={5}/>
              <Text color="link_accent">Sign Out</Text>
            </Box>
          </Box>
        </Box>
      </MenuList>
    </Menu>
  );
};

export default React.memo(UserMenu);
